import React from 'react';
import { motion } from 'framer-motion';
import { 
  UserPlusIcon, 
  ArrowPathIcon, 
  GiftIcon,
  ArrowTrendingUpIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';

/**
 * CustomerInsights - Customer analytics with Premium Light Theme
 */
const CustomerInsights = ({ insights }) => {
  if (!insights) {
    return (
      <div className="bg-white rounded-[40px] p-12 border border-gray-100 shadow-sm text-center">
        <ChartBarIcon className="h-12 w-12 text-gray-200 mx-auto mb-6" />
        <h3 className="text-xl font-black text-gray-900 tracking-tight">No Data Available</h3>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-2 font-medium">Start taking orders to see customer insights</p>
      </div>
    );
  }

  return (
    <div className="space-y-10">
      {/* Customer Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-[40px] p-10 border border-gray-100 shadow-sm relative overflow-hidden group hover:shadow-2xl hover:shadow-blue-100/30 transition-all duration-700"
        >
          <div className="absolute top-0 right-0 p-10 opacity-5 group-hover:opacity-10 transition-opacity">
             <UserPlusIcon className="h-20 w-20 text-blue-500 -mr-6 -mt-6 transition-transform duration-1000 group-hover:scale-125 group-hover:rotate-12" />
          </div>
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2 leading-none">Acquisition</p>
          <div className="flex items-baseline gap-3">
             <h3 className="text-5xl font-black text-gray-900 tracking-tighter leading-none">{insights.newCustomersToday}</h3>
             <span className="text-[10px] font-black text-blue-500 uppercase tracking-widest">New Today</span>
          </div>
          <div className="mt-8 flex items-center gap-3">
            <div className={`px-3 py-1.5 rounded-xl flex items-center gap-1.5 ${
              insights.newCustomersToday >= insights.newCustomersYesterday ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'
            }`}>
               <ArrowTrendingUpIcon className={`h-3 w-3 ${insights.newCustomersToday < insights.newCustomersYesterday && 'rotate-180'} stroke-[3]`} />
               <span className="text-xs font-black">{Math.abs(insights.newCustomersToday - insights.newCustomersYesterday)}</span>
            </div>
            <span className="text-[10px] font-bold text-gray-300 uppercase tracking-widest">vs Yesterday</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-[40px] p-10 border border-gray-100 shadow-sm relative overflow-hidden group hover:shadow-2xl hover:shadow-emerald-100/30 transition-all duration-700"
        >
          <div className="absolute top-0 right-0 p-10 opacity-5 group-hover:opacity-10 transition-opacity">
             <ArrowPathIcon className="h-20 w-20 text-emerald-500 -mr-6 -mt-6 transition-transform duration-1000 group-hover:rotate-180" />
          </div>
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2 leading-none">Customer Loyalty</p>
          <div className="flex items-baseline gap-3">
             <h3 className="text-5xl font-black text-gray-900 tracking-tighter leading-none">{insights.returningCustomers}</h3>
             <span className="text-[10px] font-black text-emerald-500 uppercase tracking-widest">Recurring</span>
          </div>
          <div className="mt-8">
            <div className="flex justify-between items-end mb-3">
              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none">{insights.returningPercentage}% Loyalty Rate</span>
            </div>
            <div className="h-2 bg-gray-50 rounded-full overflow-hidden border border-gray-100">
                <motion.div 
                  initial={{ width: 0 }}
                  animate={{ width: `${insights.returningPercentage}%` }}
                  transition={{ duration: 1.5, delay: 0.5, type: "spring" }}
                  className="h-full bg-emerald-500 rounded-full shadow-lg shadow-emerald-100"
                />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-[40px] p-10 border border-gray-100 shadow-sm relative overflow-hidden group hover:shadow-2xl hover:shadow-amber-100/30 transition-all duration-700"
        >
          <div className="absolute top-0 right-0 p-10 opacity-5 group-hover:opacity-10 transition-opacity">
             <GiftIcon className="h-20 w-20 text-amber-500 -mr-6 -mt-6 transition-transform duration-1000 group-hover:scale-125 group-hover:-rotate-12" />
          </div>
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2 leading-none">Loyalty Points</p>
          <div className="flex items-baseline gap-3">
             <h3 className="text-5xl font-black text-gray-900 tracking-tighter leading-none">{insights.loyaltyPointsIssued}</h3>
             <span className="text-[10px] font-black text-amber-600 uppercase tracking-widest">Points</span>
          </div>
          <div className="mt-8 flex items-center gap-4">
             <div className="w-10 h-10 rounded-2xl bg-amber-50 flex items-center justify-center border border-amber-100 shadow-sm text-sm">
                💰
             </div>
             <div>
                <p className="text-base font-black text-gray-900 leading-none mb-1">₹{insights.loyaltyPointsValue}</p>
                <p className="text-[9px] font-black text-gray-300 uppercase tracking-widest leading-none">Total Value</p>
             </div>
          </div>
        </motion.div>
      </div>

      {/* Customer Growth Plot */}
      {insights.growthData && insights.growthData.length > 0 && (
        <div className="bg-white rounded-[40px] p-10 border border-gray-100 shadow-sm hover:shadow-xl hover:shadow-gray-200/10 transition-all duration-700">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 mb-12">
            <div>
              <h3 className="text-2xl font-black text-gray-900 tracking-tight">Customer Growth</h3>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1 font-medium italic">New customers over the last 30 days</p>
            </div>
            <div className="flex items-center gap-3 bg-blue-50 px-4 py-2 rounded-xl border border-blue-100">
               <div className="w-2.5 h-2.5 rounded-full bg-blue-500 shadow-xl shadow-blue-200"></div>
               <span className="text-[10px] font-black text-blue-700 uppercase tracking-widest">New Customers</span>
            </div>
          </div>
          <div className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={insights.growthData}>
                <defs>
                  <linearGradient id="colorGrowth" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#f9fafb" />
                <XAxis 
                  dataKey="date" 
                  axisLine={false}
                  tickLine={false}
                  fontSize={10}
                  fontWeight={900}
                  tick={{ fill: '#d1d5db' }}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getMonth() + 1}/${date.getDate()}`;
                  }}
                  dy={15}
                />
                <YAxis 
                  axisLine={false}
                  tickLine={false}
                  fontSize={10}
                  fontWeight={900}
                  tick={{ fill: '#d1d5db' }}
                  tickFormatter={(val) => val === 0 ? '0' : val}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#ffffff', 
                    borderRadius: '24px',
                    border: '1px solid #f3f4f6',
                    boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.1)',
                    padding: '20px'
                  }}
                  itemStyle={{ color: '#111827', fontSize: '14px', fontWeight: '900' }}
                  labelStyle={{ color: '#9ca3af', fontSize: '10px', marginBottom: '8px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.1em' }}
                  labelFormatter={(value) => {
                    const date = new Date(value);
                    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
                  }}
                  formatter={(value) => [value, 'NEW CUSTOMERS']}
                />
                <Area 
                  type="monotone" 
                  dataKey="count" 
                  stroke="#3b82f6" 
                  strokeWidth={4}
                  fillOpacity={1}
                  fill="url(#colorGrowth)"
                  activeDot={{ r: 8, fill: '#3b82f6', stroke: '#fff', strokeWidth: 4, shadow: '0 10px 15px rgba(59, 130, 246, 0.5)' }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerInsights;
